import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Sparkles, RefreshCw, AlertTriangle } from "lucide-react";

function Skeleton() {
  return (
    <div className="space-y-3" data-testid="insights-skeleton">
      <div className="h-5 bg-zinc-200/60 rounded w-3/4 animate-pulse" />
      <div className="h-3 bg-zinc-200/60 rounded w-5/6 animate-pulse" />
      <div className="h-3 bg-zinc-200/60 rounded w-4/6 animate-pulse" />
      <div className="h-3 bg-zinc-200/60 rounded w-3/6 animate-pulse" />
    </div>
  );
}

export default function AIInsights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (refresh = false) => {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const res = await api.get("/analytics/insights", { params: refresh ? { refresh: true } : {} });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't generate insights");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  return (
    <div
      data-testid="ai-insights"
      className="relative rounded-xl border border-zinc-200 bg-white overflow-hidden animate-fade-up"
    >
      {/* Gradient accent strip */}
      <div
        aria-hidden
        className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-indigo-500 via-fuchsia-500 to-amber-400"
      />

      <div className="p-6 pl-7">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white flex items-center justify-center shadow-sm">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <div className="label-kicker text-indigo-700">AI insights</div>
              <div className="font-display text-sm font-medium text-zinc-900 leading-tight mt-0.5">
                What the data is telling you
              </div>
            </div>
          </div>
          <button
            data-testid="insights-refresh-btn"
            onClick={() => load(true)}
            disabled={loading || refreshing}
            className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-xs text-zinc-600 hover:bg-zinc-50 disabled:opacity-50"
            title="Regenerate insights"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Regenerate
          </button>
        </div>

        {loading ? (
          <Skeleton />
        ) : error ? (
          <div
            data-testid="insights-error"
            className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2 text-sm"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        ) : data ? (
          <div>
            <p
              data-testid="insights-headline"
              className="font-display text-lg font-medium text-zinc-950 leading-snug tracking-tight"
            >
              {data.headline}
            </p>
            <ul className="mt-3 space-y-1.5" data-testid="insights-bullets">
              {(data.bullets || []).map((b, i) => (
                <li key={i} className="flex gap-2 text-sm text-zinc-700 leading-relaxed">
                  <span className="mt-[7px] shrink-0 w-1.5 h-1.5 rounded-full bg-gradient-to-r from-indigo-500 to-fuchsia-500" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex items-center gap-2 text-[11px] text-zinc-400">
              <span>
                {data.cached ? "Cached" : "Fresh"} · powered by {data.model || "AI"}
              </span>
              {data.generated_at ? (
                <>
                  <span className="text-zinc-300">·</span>
                  <span>
                    {new Date(data.generated_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
